import api from 'utils/api';
import { Link } from 'react-router';
import React, { Component } from 'react';
import PromiseButton from 'shared/components/PromiseButton';
import cx from 'classnames';
import styles from './SharedNotificationsWidget.module.scss';
import PropTypes from 'prop-types';

class Wrapper extends Component {
  constructor() {
    super();
    this.state = {
      notifications: null,
    };
  }

  componentDidMount() {
    this.fetch();
  }

  dismissNotification = notificationId => {
    return api
      .put(`/agent_portal/api/passive_app_notifications/${notificationId}/dismiss`)
      .then(_response => {
        // Update client-side state to mark as dismissed without a hard refresh from server
        this.setState({
          notifications: this.state.notifications.map(notification => {
            return notification.id === notificationId
              ? { ...notification, dismissed: true }
              : notification;
          }),
        });
        return null;
      });
  };

  dismissAllNotifications = notificationId => {
    const url = `/agent_portal/api/passive_app_notifications/dismiss_all?first_id=${
      this.state.notifications[0].id
    }&last_id=${this.state.notifications[this.state.notifications.length - 1].id}`;
    return api.post(url).then(_response => {
      if (this.props.refreshOnDismissAll) {
        return this.fetch();
      } else {
        // Mark all as dismissed
        this.setState({
          notifications: this.state.notifications.map(notification => {
            return { ...notification, dismissed: true };
          }),
        });
      }
    });
  };

  fetch = () => {
    // Fetch initial notifications
    api.get(this.props.fetchNotificationsUrl).then(response => {
      this.setState({
        notifications: response.data.notifications,
        moreAvailable: response.data.moreAvailable,
      });
    });
  };

  fetchMore = () => {
    // Appends notifications when we "Load More"
    const lastId = this.state.notifications[this.state.notifications.length - 1].id;
    api.get(`${this.props.fetchNotificationsUrl}?last_id=${lastId}`).then(response => {
      this.setState({
        notifications: [...this.state.notifications, ...response.data.notifications],
        moreAvailable: response.data.moreAvailable,
      });
    });
  };

  render() {
    return (
      <PassiveAppNotifications
        notifications={this.state.notifications}
        moreAvailable={this.state.moreAvailable}
        dismiss={this.dismissNotification}
        dismissAll={this.dismissAllNotifications}
        friendlyCollectionName={this.props.friendlyCollectionName}
        fetchMore={this.fetchMore}
      />
    );
  }
}

Wrapper.propTypes = {
  refreshOnDismissAll: PropTypes.bool.isRequired, // true means the notifications are refreshed from the server from scratch when you dismiss all, false means the notifications are marked as dismissed.
  friendlyCollectionName: PropTypes.string.isRequired, // used to display a message when there are no notifications example: "notifications" or "unread notifications".
  fetchNotificationsUrl: PropTypes.string.isRequired, // The API url where we should fetch notifications. This is different for unread vs. all. Example: "/agent_portal/api/passive_app_notifications/unread"
};

const PassiveAppNotifications = ({
  notifications,
  dismiss,
  friendlyCollectionName,
  moreAvailable,
  fetchMore,
  dismissAll,
}) => {
  if (!notifications) return <p>Loading Notifications...</p>;
  if (notifications.length === 0) return <p>You have no {friendlyCollectionName} at this time</p>;
  return (
    <div>
      <div>
        <PromiseButton className="btn btn-primary" onClick={dismissAll}>
          Dismiss All
        </PromiseButton>
      </div>
      <br />
      <table className="table table-bordered">
        <tbody>
          {notifications.map(notification => (
            <PassiveAppNotification
              key={notification.id}
              notification={notification}
              dismiss={() => dismiss(notification.id)}
            />
          ))}
        </tbody>
      </table>
      {moreAvailable ? (
        <PromiseButton type="button" className="btn btn-primary" onClick={fetchMore}>
          Load More
        </PromiseButton>
      ) : null}
    </div>
  );
};

const PassiveAppNotification = ({ notification, dismiss }) => {
  switch (notification.type) {
    case 'ReferralCommentPassiveAppNotification':
      return (
        <ReferralCommentPassiveAppNotification notification={notification} dismiss={dismiss} />
      );
    case 'ReferralRejectionPassiveAppNotification':
      return (
        <ReferralRejectionPassiveAppNotification notification={notification} dismiss={dismiss} />
      );
    default:
      return null;
  }
};

const NotificationRow = ({ notification, dismiss, children }) => (
  <tr
    className={cx({ [styles.dismissed]: notification.dismissed })}
    data-spec-id={`passive-app-notification-${notification.id}`}
  >
    <td>{children}</td>
    <td>{notification.timeAgo} ago</td>
    <td className={styles.dismissalCell}>
      {notification.dismissed ? null : (
        <PromiseButton className="btn btn-primary btn-mini" onClick={dismiss}>
          Dismiss
        </PromiseButton>
      )}
    </td>
  </tr>
);

const ReferralRejectionPassiveAppNotification = ({ notification, dismiss }) => (
  <NotificationRow notification={notification} dismiss={dismiss}>
    {notification.involvedUserName} rejected {notification.patientName}'s{' '}
    {notification.specialtyName} referral.
    <br />
    <Link to={`/a/portal/referrals/${notification.referralId}`}>View Referral</Link>
  </NotificationRow>
);

const ReferralCommentPassiveAppNotification = ({ notification, dismiss }) => (
  <NotificationRow notification={notification} dismiss={dismiss}>
    {notification.involvedUserName} commented on {notification.patientName}'s{' '}
    {notification.specialtyName} referral.
    <br />
    <Link to={`/a/portal/referrals/${notification.referralId}`}>View Referral</Link>
  </NotificationRow>
);

export default Wrapper;
